import { useMemo, useState } from "react";
import { BillViewOptionGroup, BillViewOptions } from "@common/types/bill";
import { useOverlays } from "contexts/OverlayContext";
import { BillsViewContext, BillViewFilters } from "contexts/BillsViewContext";
import { BillsDrilldownDrawer } from "./BillsDrilldownDrawer";
import { defaultBillsView } from "./views/defaultBillViews";

// Standalone BillsViewContext for this overlay: it doesn't live inside the
// Bills page tree (mounted once, globally, in OverlaysProvider), so it can't
// rely on that page's cached filters/view-option state. Only the filters
// passed in via the overlay (see openOverlay("billsDrilldown", ...)) matter
// here; the rest is unused scaffolding to satisfy the context's shape.
function useStandaloneBillsViewContextValue() {
    const [filters, setFilters] = useState<BillViewFilters | undefined>();
    const [viewOption, setViewOption] = useState<BillViewOptions>({
        groupBy: BillViewOptionGroup.BILL,
    });

    return useMemo(
        () => ({ filters, setFilters, viewOption, setViewOption }),
        [filters, viewOption],
    );
}

// Global overlay wrapper around BillsDrilldownDrawer, for callers that don't
// already render their own local instance (e.g. the counterparty directory's
// Bills column). Mirrors GlobalTransactionViewDrawer.
export const GlobalBillsDrilldownDrawer: React.FC = () => {
    const { getOverlayInfo, closeOverlay, overlayIsClosing } = useOverlays();
    const overlayInfo = getOverlayInfo("billsDrilldown");
    const contextValue = useStandaloneBillsViewContextValue();

    if (!overlayInfo?.data) {
        return null;
    }

    return (
        <BillsViewContext.Provider value={contextValue}>
            <BillsDrilldownDrawer
                view={defaultBillsView}
                open={!overlayIsClosing}
                onClose={closeOverlay}
                parentFilters={overlayInfo.data.filters}
            />
        </BillsViewContext.Provider>
    );
};
